import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import EmojiPicker from "emoji-picker-react";
import "./EditPosts.css";
import CreatePostForm from "./CreatePostForm";
import p from './images/profile.jpg';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { FaTrash, FaRegComment, FaHeart, FaRegHeart, FaTimes } from 'react-icons/fa';
import { BsEmojiSmile } from 'react-icons/bs';
import { FiSend } from 'react-icons/fi';
import {
  FaEllipsisV, FaSave, FaRegSave, FaLink, FaFlag,
  FaThumbsDown, FaBullhorn, FaExclamationTriangle,
  FaSkullCrossbones, FaBan
} from 'react-icons/fa';

// ── Performance logger ──────────────────────────────────────────────────────
const perf = {
  marks: {},
  start(label) {
    this.marks[label] = performance.now();
    console.debug(`[PERF START] ${label}`);
  },
  end(label) {
    const t0 = this.marks[label];
    if (t0 == null) { console.warn(`[PERF] No start mark for: ${label}`); return; }
    const ms = (performance.now() - t0).toFixed(1);
    const emoji = ms > 2000 ? '🔴' : ms > 800 ? '🟡' : '🟢';
    console.log(`[PERF END] ${emoji} ${label} — ${ms}ms`);
    delete this.marks[label];
    return parseFloat(ms);
  },
  log(msg, data) { console.log(`[INFO] ${msg}`, data ?? ''); },
  error(msg, err) { console.error(`[ERROR] ${msg}`, err ?? ''); },
  warn(msg, data) { console.warn(`[WARN] ${msg}`, data ?? ''); },
};

// ── Skeleton card ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="card-edit skeleton-card">
    <div className="card-body-edit">
      <div className="Posts_header-edit">
        <div className="logo-and-text-edit">
          <div className="skeleton skeleton-avatar" />
          <div>
            <div className="skeleton skeleton-line" style={{ width: 120 }} />
            <div className="skeleton skeleton-line" style={{ width: 80, marginTop: 6 }} />
          </div>
        </div>
      </div>
      <div className="skeleton skeleton-line" style={{ width: '90%', marginTop: 14 }} />
      <div className="skeleton skeleton-line" style={{ width: '75%', marginTop: 8 }} />
      <div className="skeleton skeleton-media" />
    </div>
  </div>
);

// ── Single PostCard component (memoised to avoid re-renders) ─────────────────
const PostCard = React.memo(({
  post, isOwnPost, userId, token, API_URL,
  likeCounts, likedPosts, savedPosts,
  showOptions, showComments, comments,
  commentText, emojiPickerVisible,
  reportReasonsVisible, followText,
  commentLikeCounts, likedComments,
  replyLikeCounts, likedReplies, activeReplies, expandedReplies,
  myPostsProfileImages, suggestedPostsProfileImages,
  emojiPickerRef, optionsRef,
  onLike, onToggleComments, onCommentTextChange, onCommentSubmit,
  onReplySubmit, onCommentLike, onReplyLike, onToggleReply,
  onToggleRepliesVisibility, onToggleOptions, onSave, onCopyLink,
  onReport, onReportReasonSelect, onNotInterested, onFollow,
  onDeletePost, onHideComment, onHideReply, onDeleteComment,
  onDeleteReply, onShowDeleteCommentConfirmation, onShowDeleteReplyConfirmation,
  onPostNameClick, onToggleEmojiPicker,
}) => {
  const profileImg = isOwnPost
    ? (myPostsProfileImages[post.postid] || p)
    : (suggestedPostsProfileImages[post.postid] || p);

  return (
    <div className={`card-edit${isOwnPost ? ' own-post-card' : ''}`}>
      {/* Top badge for own posts */}
      {isOwnPost && <div className="own-post-badge">Your Post</div>}

      <div className="card-body-edit">
        {/* Header */}
        <div className="Posts_header-edit">
          <div className="logo-and-text-edit">
            <img
              src={profileImg}
              alt="Logo"
              className="logo-image-edit"
              loading="lazy"
              onError={e => { e.target.src = p; }}
            />
            <div
              className="Posts_paragraph-edit"
              onClick={() => onPostNameClick(post.page_id)}
              style={{ cursor: 'pointer' }}
            >
              <p>{post.page_name}</p>
              <p>{post.username}</p>
            </div>
          </div>

          {isOwnPost ? (
            /* Own post: show delete */
            <div className="post-owner-actions">
              <button className="delete-post-btn-edit" onClick={() => onDeletePost(post.postid)}>
                <FaTrash /> Delete
              </button>
            </div>
          ) : (
            /* Other post: follow + options */
            <div className="header-right-edit">
              <button
                className={`follow-btn-edit${followText[post.page_id] === '✓ Following' ? ' following' : ''}`}
                onClick={() => onFollow(post.page_id)}
                type="button"
              >
                {followText[post.page_id] || '+ Follow'}
              </button>
              <div className="more-options-edit">
                <button className="more-btn-edit" onClick={() => onToggleOptions(post.postid)}>
                  <FaEllipsisV />
                </button>
                {showOptions[post.postid] && (
                  <div className="dropdown-options-edit" ref={optionsRef}>
                    <button className="dropdown-item-edit" onClick={() => onSave(post.postid)}>
                      {savedPosts[post.postid] ? <FaRegSave /> : <FaSave />}
                      <span>{savedPosts[post.postid] ? 'Unsave' : 'Save'}</span>
                    </button>
                    <button className="dropdown-item-edit" onClick={() => onCopyLink(post.postid)}>
                      <FaLink /><span>Copy Link</span>
                    </button>
                    <button className="dropdown-item-edit" onClick={() => onReport(post.postid)}>
                      <FaFlag /><span>Report Post</span>
                    </button>
                    {reportReasonsVisible[post.postid] && (
                      <div className="report-reasons-edit">
                        {[
                          { icon: <FaBullhorn />, label: 'Spam' },
                          { icon: <FaSkullCrossbones />, label: 'Hate Speech' },
                          { icon: <FaExclamationTriangle />, label: 'Harassment' },
                          { icon: <FaBan />, label: 'Inappropriate Content' },
                        ].map(({ icon, label }) => (
                          <button key={label} className="report-reason-item-edit"
                            onClick={() => onReportReasonSelect(label, post.postid)}>
                            {icon}<span>{label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <button className="dropdown-item-edit" onClick={() => onNotInterested(post.postid)}>
                      <FaThumbsDown /><span>Not Interested</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="Posts_content-edit">
          <p>{post.post_content}</p>
        </div>

        {/* Media */}
        {post.image && (
          <img
            src={post.image}
            alt="Post"
            className="Posts_image-edit"
            loading="lazy"
            onError={e => { e.target.style.display = 'none'; }}
          />
        )}
        {post.video && (
          <video controls className="uploaded-video-edit" preload="metadata">
            <source src={post.video} type="video/mp4" />
          </video>
        )}

        {/* Meta row */}
        <div className="post-meta-row">
          {post.likes > 0 && (
            <span className="meta-likes">
              <FaHeart style={{ color: 'var(--like-red)', fontSize: 11 }} /> {post.likes}
            </span>
          )}
          <span className="meta-time">
            {post.created_at ? new Date(post.created_at).toLocaleDateString('en-IN', {
              day: 'numeric', month: 'short', year: 'numeric'
            }) : ''}
          </span>
        </div>

        {/* Actions */}
        <div className="post-actions">
          <button
            className={`action-btn${likedPosts[post.postid] ? ' liked' : ''}`}
            onClick={() => onLike(post.postid)}
            type="button"
          >
            {likedPosts[post.postid] ? <FaHeart /> : <FaRegHeart />}
            <span>{likeCounts[post.postid] ?? post.likes ?? 0}</span>
          </button>

          <button
            className="action-btn"
            onClick={() => onToggleComments(post.postid)}
            type="button"
          >
            <FaRegComment />
            <span>
              {showComments[post.postid] ? 'Hide' : 'Comment'}
            </span>
          </button>
        </div>
      </div>

      {/* Comments */}
      {showComments[post.postid] && (
        <div className="comments-section-edit">
          <div className="comments-header-edit">
            <h3>Comments</h3>
            <button className="close-comments-btn-edit"
              onClick={() => onToggleComments(post.postid)}>
              <FaTimes />
            </button>
          </div>

          <div className="comments-list-edit">
            {(comments[post.postid] || []).length === 0 && (
              <p className="no-comments-text">No comments yet. Be first!</p>
            )}
            {(comments[post.postid] || []).map(comment => {
              const isCommentOwner = userId && String(comment.user_id) === String(userId);
              const replyCount = comment.replies?.length || 0;
              const showReplies = expandedReplies[comment.comment_id];
              return (
                <div key={comment.comment_id} className="comment-edit">
                  <div className="comment-header-edit">
                    <img src={comment.user_avatar || p} alt="User" className="comment-user-img-edit" loading="lazy" />
                    <div className="comment-user-info-edit">
                      <span className="comment-username-edit">{comment.username}</span>
                      <p className="comment-text-edit">{comment.comment_text}</p>
                    </div>
                    {(isCommentOwner || post.user_id === userId) && (
                      <div className="comment-options-container-edit">
                        {isCommentOwner && (
                          <button className="comment-options-edit"
                            onClick={() => onShowDeleteCommentConfirmation(post.postid, comment.comment_id)}>
                            <FaTrash /> Delete
                          </button>
                        )}
                        {post.user_id === userId && (
                          <button className="hide-comment-btn-edit"
                            onClick={() => onHideComment(post.postid, comment.comment_id)}>
                            Hide
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="comment-actions-edit">
                    <button className={`like-btn-edit${likedComments[comment.comment_id] ? ' liked' : ''}`}
                      onClick={() => onCommentLike(comment.comment_id)}>
                      {likedComments[comment.comment_id] ? <FaHeart /> : <FaRegHeart />}
                      <span>{commentLikeCounts[comment.comment_id] || 0}</span>
                    </button>
                    <button className="reply-btn-edit" onClick={() => onToggleReply(comment.comment_id)}>Reply</button>
                    {replyCount > 0 && (
                      <button className="view-replies-btn-edit"
                        onClick={() => onToggleRepliesVisibility(comment.comment_id)}>
                        {replyCount === 1 ? `View ${replyCount} Reply` : `View ${replyCount} Replies`}
                      </button>
                    )}
                  </div>

                  {activeReplies[comment.comment_id] && (
                    <form className="reply-form-edit"
                      onSubmit={e => {
                        e.preventDefault();
                        const replyText = e.target.replyText.value;
                        if (replyText.trim()) { onReplySubmit(comment.comment_id, replyText); e.target.replyText.value = ''; }
                      }}>
                      <input type="text" name="replyText" placeholder="Write a reply..." className="reply-input-edit" />
                      <button type="submit" className="send-reply-btn-edit"><FiSend /></button>
                      <button type="button" className="cancel-reply-btn-edit"
                        onClick={() => onToggleReply(comment.comment_id)}>Cancel</button>
                    </form>
                  )}

                  {showReplies && comment.replies?.length > 0 && (
                    <div className="replies-list-edit">
                      {comment.replies.map(reply => {
                        const isReplyOwner = userId && String(reply.reply_user_id) === String(userId);
                        return (
                          <div key={reply.reply_id} className="reply-edit">
                            <div className="reply-header-edit">
                              <img src={reply.reply_avatar || p} alt="User" className="reply-user-img-edit" loading="lazy" />
                              <div className="reply-user-info-edit">
                                <span className="reply-username-edit">{reply.reply_username}</span>
                                <p className="reply-text-edit">{reply.reply_text}</p>
                              </div>
                              {(isReplyOwner || post.user_id === userId) && (
                                <div className="reply-options-container-edit">
                                  {isReplyOwner && (
                                    <button className="reply-options-edit"
                                      onClick={() => onShowDeleteReplyConfirmation(reply.reply_id)}>
                                      <FaTrash /> Delete
                                    </button>
                                  )}
                                  {post.user_id === userId && (
                                    <button className="hide-reply-btn-edit"
                                      onClick={() => onHideReply(post.postid, reply.reply_id)}>Hide</button>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="reply-actions-edit">
                              <button className={`like-btn-edit${likedReplies[reply.reply_id] ? ' liked' : ''}`}
                                onClick={() => onReplyLike(reply.reply_id)}>
                                {likedReplies[reply.reply_id] ? <FaHeart /> : <FaRegHeart />}
                                <span>{replyLikeCounts[reply.reply_id] || 0}</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <form className="comments-form-edit"
            onSubmit={e => {
              e.preventDefault();
              if (commentText[post.postid]?.trim()) {
                onCommentSubmit(post.postid, commentText[post.postid]);
              }
            }}>
            <input
              type="text"
              className="comment-input-edit"
              placeholder="Write a comment…"
              value={commentText[post.postid] || ''}
              onChange={e => onCommentTextChange(e, post.postid)}
            />
            <button type="button" className="emoji-btn-edit" onClick={onToggleEmojiPicker}>
              <BsEmojiSmile />
            </button>
            {emojiPickerVisible && (
              <div className="emoji-picker-container-edit" ref={emojiPickerRef}>
                <button className="close-emoji-picker-btn-edit" onClick={() => onToggleEmojiPicker()}>
                  <FaTimes />
                </button>
                <EmojiPicker
                  onEmojiClick={emojiObj => onCommentTextChange(
                    { target: { value: (commentText[post.postid] || '') + emojiObj.emoji } },
                    post.postid
                  )}
                />
              </div>
            )}
            <button type="submit" className="submit-btn-edit" disabled={!commentText[post.postid]?.trim()}>
              Post
            </button>
          </form>
        </div>
      )}
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────
function EditPosts() {
  const [showOptions, setShowOptions]     = useState({});
  const [showComments, setShowComments]   = useState({});
  const [commentText, setCommentText]     = useState({});
  const [message, setMessage]             = useState('');
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const [createPostVisible, setCreatePostVisible]   = useState(false);
  const [posts, setPosts]                 = useState([]);           // MY posts
  const [suggestedPosts, setSuggestedPosts] = useState([]);         // OTHER posts
  const [savedPosts, setSavedPosts]       = useState({});
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [likeCounts, setLikeCounts]       = useState({});
  const [likedPosts, setLikedPosts]       = useState({});
  const [uploadMessage, setUploadMessage] = useState('');
  const [showUploadMessage, setShowUploadMessage] = useState(false);
  const [reportReasonsVisible, setReportReasonsVisible] = useState({});
  const [userId, setUserId]               = useState(null);
  const [pageId, setPageId]               = useState(null);
  const token = localStorage.getItem('token');

  const [comments, setComments]           = useState({});
  const [activeReplies, setActiveReplies] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});
  const [commentLikeCounts, setCommentLikeCounts] = useState({});
  const [replyLikeCounts, setReplyLikeCounts]     = useState({});
  const [likedComments, setLikedComments] = useState({});
  const [likedReplies, setLikedReplies]   = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, type: null, id: null, postid: null });
  const [followText, setFollowText]       = useState({});

  const [myPostsProfileImages, setMyPostsProfileImages]             = useState({});
  const [suggestedPostsProfileImages, setSuggestedPostsProfileImages] = useState({});

  const API_URL   = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
  const optionsRef    = useRef(null);
  const emojiPickerRef = useRef(null);
  const navigate  = useNavigate();
  const prefetchQueue = useRef(new Set()); // track which posts we've already prefetched

  const { userId: contextUserId, pageId: contextPageId } = useOutletContext();

  useEffect(() => {
    if (contextUserId) setUserId(contextUserId);
    if (contextPageId) setPageId(contextPageId);
  }, [contextUserId, contextPageId]);

  // ── Resolve user id from token (fallback) ──────────────────────────────────
  useEffect(() => {
    if (contextUserId) return; // already set via context
    const fetchUserId = async () => {
      try {
        const r = await fetch(`${API_URL}/api/get-user-id`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) { perf.error('fetchUserId', r.statusText); return; }
        const d = await r.json();
        setUserId(d.user_id);
      } catch (e) { perf.error('fetchUserId', e); }
    };
    fetchUserId();
  }, [token]);

  // ── Fetch saved posts ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/user/saved-posts?userid=${userId}`);
        if (!r.ok) { perf.error('fetchSavedPosts', r.statusText); return; }
        const d = await r.json();
        const map = {};
        d.saved_posts.forEach(id => { map[id] = true; });
        setSavedPosts(map);
      } catch (e) { perf.error('fetchSavedPosts', e); }
    })();
  }, [userId]);

  // ── Main feed fetch ────────────────────────────────────────────────────────
  const fetchFeed = useCallback(async (signal) => {
    if (!userId || !pageId) { perf.warn('fetchFeed skipped', { userId, pageId }); return; }
    perf.start('fetchFeed');
    setLoading(true);
    setError(null);

    try {
      const url = `${API_URL}/page/posts?user_id=${userId}&page_id=${pageId}`;
      perf.log('Fetching posts from', url);

      const res = await fetch(url, { signal });
      if (!res.ok) {
        perf.error('fetchFeed HTTP error', `${res.status} ${res.statusText}`);
        setError('Could not load posts. Please try again.');
        return;
      }

      const data = await res.json();
      perf.log('fetchFeed raw response', { mypostsCount: data.myposts?.length, otherpostsCount: data.otherposts?.length });

      const allPosts = [...(data.myposts || []), ...(data.otherposts || [])];
      const uniquePageIds = [...new Set(allPosts.map(p => p.page_id).filter(Boolean))];

      // Profile images from inline data
      const profileImageMap = {};
      allPosts.forEach(post => { profileImageMap[post.page_id] = post.profile_image_url || p; });

      // Follow status batch
      perf.start('followStatusBatch');
      const followStatusMap = await (async () => {
        try {
          const r = await fetch(
            `${API_URL}/api/follow-status-batch?page_ids=${uniquePageIds.join(',')}`,
            { headers: { Authorization: `Bearer ${token}` }, signal }
          );
          if (!r.ok) { perf.warn('followStatusBatch failed', r.status); return {}; }
          return await r.json();
        } catch (e) { perf.warn('followStatusBatch error', e); return {}; }
      })();
      perf.end('followStatusBatch');

      const myPostIds = new Set((data.myposts || []).map(p => p.postid));

      const processPosts = (postsData) => postsData.map(post => {
        const profileImage = profileImageMap[post.page_id] || p;
        setMyPostsProfileImages(prev => ({ ...prev, [post.postid]: profileImage }));
        setSuggestedPostsProfileImages(prev => ({ ...prev, [post.postid]: profileImage }));
        return {
          ...post,
          author: post.username,
          image: post.image_url || null,
          video: post.video_url || null,
          isFollowing: followStatusMap[String(post.page_id)] ?? false,
        };
      });

      const myPosts    = processPosts(data.myposts    || []);
      const otherPosts = processPosts(data.otherposts || []);

      // My posts: newest first (API already orders DESC, this is a safety sort)
      const sortedMyPosts = [...myPosts].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setPosts(sortedMyPosts);
      setSuggestedPosts(otherPosts);

      // Seed like/liked state
      const initLiked = {}, initCounts = {};
      [...myPosts, ...otherPosts].forEach(post => {
        initLiked[post.postid]  = post.has_liked;
        initCounts[post.postid] = post.likes || 0;
      });
      setLikedPosts(initLiked);
      setLikeCounts(initCounts);

      // Follow text
      const initFollow = {};
      otherPosts.forEach(p => { initFollow[p.page_id] = p.isFollowing ? '✓ Following' : '+ Follow'; });
      setFollowText(initFollow);

      const elapsed = perf.end('fetchFeed');
      if (elapsed > 2000) perf.error('fetchFeed SLOW', `${elapsed}ms — consider caching or pagination`);

    } catch (e) {
      if (e.name === 'AbortError') { perf.log('fetchFeed aborted'); return; }
      perf.error('fetchFeed exception', e);
      setError('An error occurred. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [userId, pageId, token, API_URL]);

  useEffect(() => {
    if (!userId || !pageId) return;
    const ctrl = new AbortController();
    fetchFeed(ctrl.signal);
    return () => ctrl.abort();
  }, [userId, pageId, fetchFeed]);

  // Save liked posts to localStorage
  useEffect(() => {
    try { localStorage.setItem('likedPosts', JSON.stringify(likedPosts)); }
    catch (e) { perf.error('localStorage write failed', e); }
  }, [likedPosts]);

  // Click outside to close options / emoji
  useEffect(() => {
    const handler = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target) &&
        !e.target.closest('.emoji-picker-container-edit')) {
        setShowOptions({});
      }
      if (emojiPickerVisible && emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target) &&
        e.target.className !== 'emoji-btn-edit') {
        setEmojiPickerVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [emojiPickerVisible]);

  // ── Prefetch comments on hover ─────────────────────────────────────────────
  const prefetchComments = useCallback((postid) => {
    if (prefetchQueue.current.has(postid) || comments[postid]) return;
    prefetchQueue.current.add(postid);
    perf.log('Prefetching comments for post', postid);
    fetch(`${API_URL}/page/post/commentshome/${postid}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setComments(prev => ({ ...prev, [postid]: data.comments }));
        const cl = {}, rl = {}, lc = {}, lr = {};
        data.comments.forEach(c => {
          cl[c.comment_id] = c.like_count || 0;
          lc[c.comment_id] = c.has_liked || false;
          (c.replies || []).forEach(r => {
            rl[r.reply_id] = r.like_count || 0;
            lr[r.reply_id] = r.has_liked || false;
          });
        });
        setCommentLikeCounts(prev => ({ ...prev, ...cl }));
        setReplyLikeCounts(prev => ({ ...prev, ...rl }));
        setLikedComments(prev => ({ ...prev, ...lc }));
        setLikedReplies(prev => ({ ...prev, ...lr }));
      })
      .catch(e => { perf.warn('prefetchComments error', e); prefetchQueue.current.delete(postid); });
  }, [comments, API_URL]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleLikeClick = useCallback(async (postid) => {
    // Optimistic update
    setLikedPosts(prev => ({ ...prev, [postid]: !prev[postid] }));
    setLikeCounts(prev => ({ ...prev, [postid]: (prev[postid] || 0) + (likedPosts[postid] ? -1 : 1) }));
    try {
      perf.start(`like:${postid}`);
      const r = await fetch(`${API_URL}/page/post/like/${postid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      perf.end(`like:${postid}`);
      if (r.ok) {
        const d = await r.json();
        setLikeCounts(prev => ({ ...prev, [postid]: d.likes }));
      } else {
        perf.error('handleLikeClick', `${r.status} ${r.statusText}`);
        // Revert optimistic
        setLikedPosts(prev => ({ ...prev, [postid]: !prev[postid] }));
        setLikeCounts(prev => ({ ...prev, [postid]: Math.max(0, (prev[postid] || 0) - 1) }));
      }
    } catch (e) { perf.error('handleLikeClick exception', e); }
  }, [userId, likedPosts, API_URL]);

  const handleToggleComments = useCallback((postid) => {
    setShowComments(prev => {
      const willOpen = !prev[postid];
      if (willOpen && !comments[postid]) {
        perf.start(`fetchComments:${postid}`);
        fetchComments(postid).then(() => perf.end(`fetchComments:${postid}`));
      }
      return { ...prev, [postid]: willOpen };
    });
  }, [comments]);

  const fetchComments = useCallback(async (postid) => {
    try {
      const r = await fetch(`${API_URL}/page/post/commentshome/${postid}`);
      if (!r.ok) { perf.error('fetchComments', `${r.status}`); return; }
      const data = await r.json();
      setComments(prev => ({ ...prev, [postid]: data.comments }));
      const cl = {}, rl = {}, lc = {}, lr = {};
      data.comments.forEach(c => {
        cl[c.comment_id] = c.like_count || 0;
        lc[c.comment_id] = c.has_liked || false;
        (c.replies || []).forEach(rep => {
          rl[rep.reply_id] = rep.like_count || 0;
          lr[rep.reply_id] = rep.has_liked || false;
        });
      });
      setCommentLikeCounts(prev => ({ ...prev, ...cl }));
      setReplyLikeCounts(prev => ({ ...prev, ...rl }));
      setLikedComments(prev => ({ ...prev, ...lc }));
      setLikedReplies(prev => ({ ...prev, ...lr }));
    } catch (e) { perf.error('fetchComments exception', e); }
  }, [API_URL]);

  const handleCommentSubmit = useCallback(async (postid, text) => {
    if (!text?.trim()) return;
    // Optimistic: show immediately
    const tempComment = { comment_id: `temp_${Date.now()}`, username: 'You', comment_text: text, replies: [] };
    setComments(prev => ({ ...prev, [postid]: [...(prev[postid] || []), tempComment] }));
    setCommentText(prev => ({ ...prev, [postid]: '' }));
    try {
      const formData = new FormData();
      formData.append('postid', postid);
      formData.append('user_id', userId);
      formData.append('comment_text', text);
      const r = await fetch(`${API_URL}/page/post/commenthome`, { method: 'POST', body: formData });
      if (r.ok) {
        await fetchComments(postid); // refresh with real data
      } else {
        perf.error('handleCommentSubmit', r.statusText);
        // Remove temp comment on failure
        setComments(prev => ({ ...prev, [postid]: (prev[postid] || []).filter(c => c.comment_id !== tempComment.comment_id) }));
      }
    } catch (e) { perf.error('handleCommentSubmit exception', e); }
  }, [userId, fetchComments, API_URL]);

  const handleReplySubmit = useCallback(async (commentid, replyText) => {
    try {
      const formData = new FormData();
      formData.append('comment_id', commentid);
      formData.append('user_id', userId);
      formData.append('reply_text', replyText);
      const r = await fetch(`${API_URL}/page/post/replyhome`, { method: 'POST', body: formData });
      if (r.ok) {
        const postid = Object.keys(comments).find(pid =>
          comments[pid].some(c => c.comment_id === commentid)
        );
        if (postid) await fetchComments(postid);
        setActiveReplies(prev => ({ ...prev, [commentid]: false }));
        setExpandedReplies(prev => ({ ...prev, [commentid]: true }));
      } else { perf.error('handleReplySubmit', r.statusText); }
    } catch (e) { perf.error('handleReplySubmit exception', e); }
  }, [userId, comments, fetchComments, API_URL]);

  const handleCommentLikeClick = useCallback(async (commentId) => {
    setLikedComments(prev => ({ ...prev, [commentId]: !prev[commentId] }));
    setCommentLikeCounts(prev => ({ ...prev, [commentId]: (prev[commentId] || 0) + (likedComments[commentId] ? -1 : 1) }));
    try {
      const r = await fetch(`${API_URL}/api/comment/like`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment_id: commentId, user_id: userId }),
      });
      if (r.ok) {
        const d = await r.json();
        setCommentLikeCounts(prev => ({ ...prev, [commentId]: d.likes }));
      } else { perf.error('handleCommentLikeClick', r.statusText); }
    } catch (e) { perf.error('handleCommentLikeClick exception', e); }
  }, [userId, likedComments, API_URL]);

  const handleReplyLikeClick = useCallback(async (replyId) => {
    setLikedReplies(prev => ({ ...prev, [replyId]: !prev[replyId] }));
    try {
      const r = await fetch(`${API_URL}/api/reply/like`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply_id: replyId, user_id: userId }),
      });
      if (r.ok) {
        const d = await r.json();
        setReplyLikeCounts(prev => ({ ...prev, [replyId]: d.likes }));
      }
    } catch (e) { perf.error('handleReplyLikeClick exception', e); }
  }, [userId, API_URL]);

  const handleDeletePost = useCallback(async (postid) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      perf.start(`deletePost:${postid}`);
      const identity = userId || localStorage.getItem('userEmail');
      const r = await fetch(`${API_URL}/page/post/delete/${postid}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ identity }),
      });
      perf.end(`deletePost:${postid}`);
      if (r.ok) {
        setPosts(prev => prev.filter(p => p.postid !== postid));
        setSuggestedPosts(prev => prev.filter(p => p.postid !== postid));
        setMessage('Post deleted!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const d = await r.json();
        perf.error('handleDeletePost', d.error);
        setError(d.error || 'Failed to delete post');
      }
    } catch (e) { perf.error('handleDeletePost exception', e); setError('An error occurred'); }
  }, [userId, token, API_URL]);

  const handleSaveClick = useCallback(async (postid) => {
    const newState = !savedPosts[postid];
    setSavedPosts(prev => ({ ...prev, [postid]: newState })); // optimistic
    try {
      const r = await fetch(`${API_URL}/api/post/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postid, userid: userId, is_saved: newState }),
      });
      if (!r.ok) {
        perf.error('handleSaveClick', r.statusText);
        setSavedPosts(prev => ({ ...prev, [postid]: !newState })); // revert
      } else {
        setMessage(newState ? 'Post saved!' : 'Post unsaved!');
        setTimeout(() => setMessage(''), 2000);
      }
    } catch (e) { perf.error('handleSaveClick exception', e); }
  }, [savedPosts, userId, API_URL]);

  const handleReportClick = useCallback((postid) => {
    setReportReasonsVisible(prev => ({ ...prev, [postid]: !prev[postid] }));
  }, []);

  const handleReportReasonSelect = useCallback(async (reason, postid) => {
    try {
      const r = await fetch(`${API_URL}/api/post/report`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postid, userid: userId, report_reason: reason, is_reported: true }),
      });
      if (r.ok) {
        setSuggestedPosts(prev => prev.filter(p => p.postid !== postid));
        setReportReasonsVisible({});
        setMessage('Post reported. Thank you!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const d = await r.json();
        perf.error('handleReportReasonSelect', d.error);
      }
    } catch (e) { perf.error('handleReportReasonSelect exception', e); }
  }, [userId, API_URL]);

  const handleNotInterestedClick = useCallback(async (postid) => {
    setSuggestedPosts(prev => prev.filter(p => p.postid !== postid)); // instant UI
    try {
      const r = await fetch(`${API_URL}/api/post/not_interested`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postid, userid: userId, not_interested: true }),
      });
      if (!r.ok) {
        perf.error('handleNotInterestedClick', r.statusText);
        fetchFeed(); // re-fetch to restore
      }
    } catch (e) { perf.error('handleNotInterestedClick exception', e); }
  }, [userId, API_URL, fetchFeed]);

  const copyLinkToClipboard = useCallback(async (postid) => {
    const link = `${window.location.origin}/post/${postid}`;
    try {
      await navigator.clipboard.writeText(link);
      setMessage('Link copied!');
      setTimeout(() => setMessage(''), 2000);
    } catch (e) { perf.error('copyLinkToClipboard', e); }
  }, []);

  const handleFollowClick = useCallback(async (pgId) => {
    try {
      const r = await fetch(`${API_URL}/api/follow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, page_id: pgId }),
      });
      if (r.ok) {
        const d = await r.json();
        setFollowText(prev => ({ ...prev, [pgId]: d.following ? '✓ Following' : '+ Follow' }));
      } else { perf.error('handleFollowClick', r.statusText); }
    } catch (e) { perf.error('handleFollowClick exception', e); }
  }, [userId, token, API_URL]);

  const handleHideComment = useCallback(async (postid, commentid) => {
    if (!window.confirm('Hide this comment?')) return;
    try {
      const r = await fetch(`${API_URL}/api/hide_comment/${commentid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, post_id: postid }),
      });
      if (r.ok) await fetchComments(postid);
      else perf.error('handleHideComment', r.statusText);
    } catch (e) { perf.error('handleHideComment exception', e); }
  }, [userId, token, fetchComments, API_URL]);

  const handleHideReply = useCallback(async (postid, replyid) => {
    if (!window.confirm('Hide this reply?')) return;
    try {
      const r = await fetch(`${API_URL}/api/hide_reply/${replyid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, post_id: postid }),
      });
      if (r.ok) await fetchComments(postid);
      else perf.error('handleHideReply', r.statusText);
    } catch (e) { perf.error('handleHideReply exception', e); }
  }, [userId, token, fetchComments, API_URL]);

  const handleDeleteComment = useCallback(async (postid, commentid) => {
    try {
      const r = await fetch(`${API_URL}/api/delete_comment/${commentid}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (r.ok) await fetchComments(postid);
      else perf.error('handleDeleteComment', r.statusText);
    } catch (e) { perf.error('handleDeleteComment exception', e); }
  }, [userId, fetchComments, API_URL]);

  const handleDeleteReply = useCallback(async (replyid) => {
    try {
      const r = await fetch(`${API_URL}/api/delete_reply/${replyid}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (r.ok) {
        const postid = Object.keys(comments).find(pid =>
          comments[pid].some(c => c.replies?.find(rep => rep.reply_id === replyid))
        );
        if (postid) await fetchComments(postid);
      } else { perf.error('handleDeleteReply', r.statusText); }
    } catch (e) { perf.error('handleDeleteReply exception', e); }
  }, [userId, comments, fetchComments, API_URL]);

  const handlePostCreated = useCallback(() => {
    setUploadMessage('Post created! Refreshing feed…');
    setShowUploadMessage(true);
    setTimeout(() => setShowUploadMessage(false), 3000);
    fetchFeed();
  }, [fetchFeed]);

  const showDeleteCommentConfirmation = useCallback((postid, commentid) => {
    setDeleteConfirmation({ show: true, type: 'comment', id: commentid, postid });
  }, []);

  const showDeleteReplyConfirmation = useCallback((replyid) => {
    setDeleteConfirmation({ show: true, type: 'reply', id: replyid, postid: null });
  }, []);

  const handleCommentInputChange = useCallback((e, postid) => {
    setCommentText(prev => ({ ...prev, [postid]: e.target.value }));
  }, []);

  const toggleOptions = useCallback((postid) => {
    setShowOptions(prev => ({ ...prev, [postid]: !prev[postid] }));
  }, []);

  const toggleEmojiPicker = useCallback(() => {
    setEmojiPickerVisible(v => !v);
  }, []);

  // ── Shared PostCard props helper ───────────────────────────────────────────
  const sharedCardProps = {
    userId, token, API_URL,
    likeCounts, likedPosts, savedPosts,
    showOptions, showComments, comments,
    commentText, emojiPickerVisible,
    reportReasonsVisible, followText,
    commentLikeCounts, likedComments,
    replyLikeCounts, likedReplies, activeReplies, expandedReplies,
    myPostsProfileImages, suggestedPostsProfileImages,
    emojiPickerRef, optionsRef,
    onLike: handleLikeClick,
    onToggleComments: handleToggleComments,
    onCommentTextChange: handleCommentInputChange,
    onCommentSubmit: handleCommentSubmit,
    onReplySubmit: handleReplySubmit,
    onCommentLike: handleCommentLikeClick,
    onReplyLike: handleReplyLikeClick,
    onToggleReply: (id) => setActiveReplies(prev => ({ ...prev, [id]: !prev[id] })),
    onToggleRepliesVisibility: (id) => setExpandedReplies(prev => ({ ...prev, [id]: !prev[id] })),
    onToggleOptions: toggleOptions,
    onSave: handleSaveClick,
    onCopyLink: copyLinkToClipboard,
    onReport: handleReportClick,
    onReportReasonSelect: handleReportReasonSelect,
    onNotInterested: handleNotInterestedClick,
    onFollow: handleFollowClick,
    onDeletePost: handleDeletePost,
    onHideComment: handleHideComment,
    onHideReply: handleHideReply,
    onDeleteComment: handleDeleteComment,
    onDeleteReply: handleDeleteReply,
    onShowDeleteCommentConfirmation: showDeleteCommentConfirmation,
    onShowDeleteReplyConfirmation: showDeleteReplyConfirmation,
    onPostNameClick: (pgId) => navigate(`/store/${pgId}`),
    onToggleEmojiPicker: toggleEmojiPicker,
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="feed-root">
      {showUploadMessage && <div className="upload-message">{uploadMessage}</div>}

      {/* ── MY POSTS SECTION ─────────────────────────────────────────────── */}
      <section className="feed-section">
        <div className="feed-section-header">
          <div className="feed-section-header-left">
            <span className="feed-section-icon">📝</span>
            <div>
              <h2 className="feed-section-title">My Posts</h2>
              {!loading && (
                <p className="feed-section-sub">
                  {posts.length === 0 ? 'No posts yet' : `${posts.length} post${posts.length !== 1 ? 's' : ''}`}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="create-post-inline-btn"
            onClick={() => setCreatePostVisible(true)}
          >
            <span className="create-post-plus">+</span>
            <span>New Post</span>
          </button>
        </div>

        {loading ? (
          <div className="skeleton-feed">
            {[1, 2].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="error-state-edit">
            <p>{error}</p>
            <button className="retry-btn-edit" onClick={() => fetchFeed()}>Retry</button>
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state-edit">
            <span className="empty-icon">✍️</span>
            <p>You haven't posted anything yet.</p>
            <button className="retry-btn-edit" onClick={() => setCreatePostVisible(true)}>
              Create Your First Post
            </button>
          </div>
        ) : (
          <div className="post-list">
            {posts.map((post) => (
              <div key={post.postid} onMouseEnter={() => prefetchComments(post.postid)}>
                <PostCard {...sharedCardProps} post={post} isOwnPost={true} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── SUGGESTED POSTS SECTION ──────────────────────────────────────── */}
      <section className="feed-section feed-section--suggested">
        <div className="feed-section-header">
          <div className="feed-section-header-left">
            <span className="feed-section-icon">✨</span>
            <div>
              <h2 className="feed-section-title">Suggested Posts</h2>
              {!loading && (
                <p className="feed-section-sub">
                  {suggestedPosts.length === 0
                    ? 'Nothing to show'
                    : `${suggestedPosts.length} post${suggestedPosts.length !== 1 ? 's' : ''} for you`}
                </p>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="skeleton-feed">
            {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : suggestedPosts.length === 0 ? (
          <div className="empty-state-edit">
            <span className="empty-icon">🔍</span>
            <p>No suggested posts right now.</p>
          </div>
        ) : (
          <div className="post-list">
            {suggestedPosts.map((post) => (
              <div key={post.postid} onMouseEnter={() => prefetchComments(post.postid)}>
                <PostCard {...sharedCardProps} post={post} isOwnPost={false} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Delete confirmation modal ─────────────────────────────────────── */}
      {deleteConfirmation.show && (
        <div className="confirmation-dialog-overlay-edit">
          <div className="confirmation-dialog-edit">
            <h3>Delete {deleteConfirmation.type}?</h3>
            <p>This action cannot be undone.</p>
            <div className="confirmation-buttons-edit">
              <button className="cancel-btn-edit" onClick={() => setDeleteConfirmation({ show: false })}>
                Cancel
              </button>
              <button className="delete-btn-edit" onClick={() => {
                if (deleteConfirmation.type === 'comment') {
                  handleDeleteComment(deleteConfirmation.postid, deleteConfirmation.id);
                } else {
                  handleDeleteReply(deleteConfirmation.id);
                }
                setDeleteConfirmation({ show: false });
              }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {message && <div className="message-popup-edit">{message}</div>}

      {createPostVisible && (
        <CreatePostForm onClose={() => setCreatePostVisible(false)} onPostCreated={handlePostCreated} />
      )}
    </div>
  );
}

export default EditPosts;